<template>
  <div class="registration">
    <page-header />

    <main class="terms page-content">
      <h1 class="page-title">
        {{ $t("registrationPage.title") }}
      </h1>
      <p class="text">
        {{ $t("registrationPage.subTitle") }}
      </p>
      <form>
        <label class="radbox">
          <input
            v-model="isPerson"
            :value="true"
            class="radbox__input"
            type="radio"
          />
          <span class="radbox__text">{{
            $t("registrationPage.form.person")
          }}</span>
        </label>
        <label class="radbox">
          <input
            v-model="isPerson"
            :value="false"
            class="radbox__input"
            type="radio"
          />
          <span class="radbox__text">{{
            $t("registrationPage.form.legalPerson")
          }}</span>
        </label>
        <hr class="form-separator" />
        <template v-if="!isPerson">
          <input
            v-if="config.registrationFields.companyName"
            v-model="v$.form.companyName.$model"
            class="input"
            :class="{ error: v$.form.companyName.$error }"
            type="text"
            :placeholder="$t('registrationPage.form.companyName') + '*'"
          />
          <p v-if="v$.form.companyName.$error" class="input-error">
            {{
              $t("registrationPage.validation.companyName", {
                minLength: formValidationParams.companyName.minLength,
                maxLength: formValidationParams.companyName.maxLength
              })
            }}
          </p>
          <input
            v-if="config.registrationFields.taxNumber"
            v-model="v$.form.taxNumber.$model"
            class="input"
            :class="{ error: v$.form.taxNumber.$error }"
            type="text"
            :placeholder="$t('registrationPage.form.taxNumber') + '*'"
          />
          <p v-if="v$.form.taxNumber.$error" class="input-error">
            {{
              $t("registrationPage.validation.taxNumber", {
                minLength: formValidationParams.taxNumber.minLength,
                maxLength: formValidationParams.taxNumber.maxLength
              })
            }}
          </p>
          <input
            v-if="config.registrationFields.groupCode"
            v-model="v$.form.groupCode.$model"
            class="input"
            :class="{ error: v$.form.groupCode.$error }"
            type="text"
            :placeholder="$t('registrationPage.form.groupCode')"
          />
          <p v-if="v$.form.groupCode.$error" class="input-error">
            {{
              $t("registrationPage.validation.groupCode", {
                minLength: formValidationParams.groupCode.minLength,
                maxLength: formValidationParams.groupCode.maxLength
              })
            }}
          </p>
        </template>
        <template v-else>
          <input
            v-if="config.registrationFields.lastName"
            v-model="v$.form.lastName.$model"
            class="input"
            :class="{ error: v$.form.lastName.$error }"
            type="text"
            :placeholder="$t('registrationPage.form.lastName') + '*'"
          />
          <p v-if="v$.form.lastName.$error" class="input-error">
            {{
              $t("registrationPage.validation.lastName", {
                minLength: formValidationParams.lastName.minLength,
                maxLength: formValidationParams.lastName.maxLength
              })
            }}
          </p>
          <input
            v-if="config.registrationFields.firstName"
            v-model="v$.form.firstName.$model"
            class="input"
            :class="{ error: v$.form.firstName.$error }"
            type="text"
            :placeholder="$t('registrationPage.form.firstName') + '*'"
          />
          <p v-if="v$.form.firstName.$error" class="input-error">
            {{
              $t("registrationPage.validation.firstName", {
                minLength: formValidationParams.firstName.minLength,
                maxLength: formValidationParams.firstName.maxLength
              })
            }}
          </p>
          <input
            v-if="config.registrationFields.phoneNumber"
            v-model="v$.form.phoneNumber.$model"
            class="input"
            :class="{ error: v$.form.phoneNumber.$error }"
            type="text"
            :placeholder="$t('registrationPage.form.phoneNumber') + '*'"
          />
          <p v-if="v$.form.phoneNumber.$error" class="input-error">
            {{
              $t("registrationPage.validation.phoneNumber", {
                minLength: formValidationParams.phoneNumber.minLength,
                maxLength: formValidationParams.phoneNumber.maxLength
              })
            }}
          </p>
        </template>
        <hr class="form-separator" />

        <country-select
          v-if="config.registrationFields.country"
          v-model="v$.form.country.$model as string"
          :error="v$.form.country.$error"
        />
        <p v-if="v$.form.country.$error" class="input-error">
          {{ $t("registrationPage.validation.country") }}
        </p>

        <input
          v-if="config.registrationFields.postCode"
          v-model="v$.form.postCode.$model"
          class="input"
          :class="{ error: v$.form.postCode.$error }"
          type="text"
          :placeholder="$t('registrationPage.form.postCode') + '*'"
        />
        <p v-if="v$.form.postCode.$error" class="input-error">
          {{
            $t("registrationPage.validation.postCode", {
              minLength: formValidationParams.postCode.minLength,
              maxLength: formValidationParams.postCode.maxLength
            })
          }}
        </p>
        <input
          v-if="config.registrationFields.city"
          v-model="v$.form.city.$model"
          class="input"
          :class="{ error: v$.form.city.$error }"
          type="text"
          :placeholder="$t('registrationPage.form.city') + '*'"
        />
        <p v-if="v$.form.city.$error" class="input-error">
          {{
            $t("registrationPage.validation.city", {
              minLength: formValidationParams.city.minLength,
              maxLength: formValidationParams.city.maxLength
            })
          }}
        </p>
        <input
          v-if="config.registrationFields.address"
          v-model="v$.form.address.$model"
          class="input"
          :class="{ error: v$.form.address.$error }"
          type="text"
          :placeholder="$t('registrationPage.form.address') + '*'"
        />
        <p v-if="v$.form.address.$error" class="input-error">
          {{
            $t("registrationPage.validation.address", {
              minLength: formValidationParams.address.minLength,
              maxLength: formValidationParams.address.maxLength
            })
          }}
        </p>
        <button-component
          block
          primary
          :disabled="v$.form.$invalid"
          :loading="submitIsLoading"
          @click="onSubmit"
        >
          {{ $t("registrationPage.enterBankCard") }}
        </button-component>
      </form>
    </main>
    <feedback-modal
      v-if="isFeedbackModalActive"
      :main-title="$t('errors.defaultError.mainTitle')"
      :sub-title="feedbackSubTitle"
      :success="false"
      @confirm="handleFeedbackModalOkClick"
    />
  </div>
</template>

<script lang="ts" setup>
import FeedbackModal from "@/components/ui/FeedbackModal"
import { GuestChargingService } from "@/services/guest-charging-service"
import {
  maxLength,
  minLength,
  required,
  requiredIf,
  helpers
} from "@vuelidate/validators"
import config from "../config/index"
import PageHeader from "../components/ui/PageHeader.vue"
import ButtonComponent from "../components/ui/ButtonComponent.vue"
import CountrySelect from "@/components/ui/CountrySelect.vue"
import useVuelidate from "@vuelidate/core"
import { computed, onMounted, reactive, ref } from "vue"
import { useI18n } from "vue-i18n"
import { useRouter } from "vue-router"
import { useUserStore } from "../store/user"
import { storeToRefs } from "pinia"
import { stringNullify } from "@/utils/util"
import { AccountingInfo } from "@/models/common"
import { getCountryName } from "@/utils/getCountryName"
import { countries } from "@/models/countries"

// also has min-max length validation on formValidationParams
const TWO_TO_SIXTY_LETTERS_WITHOUT_DIGITS_AND_SOME_SPECIAL_CHARACTERS =
  helpers.regex(/^[^<>$%";*+?~:()=0-9]{2,60}$/i)
const TWO_TO_SIXTY_LETTERS_WITHOUT_SOME_SPECIAL_CHARACTERS = helpers.regex(
  /^[^<>$%";*+?~:=]{2,60}$/i
)
const FOUR_TO_TWELVE_LETTERS_OR_DIGITS_OR_HYPHENS =
  helpers.regex(/^[a-zA-Z0-9-]{4,12}$/i)
const FIVE_TO_150_LETTERS_WITHOUT_SOME_SPECIAL_CHARACTERS = helpers.regex(
  /^[^<>$%";*+?~:=]{5,150}$/i
)
const FIVE_TO_TWENTY_LETTERS_OR_DIGITS_OR_HYPHENS =
  helpers.regex(/^[a-zA-Z0-9-]{5,20}$/i)
const FIVE_TO_TWENTY_DIGITS = helpers.regex(/^[0-9]{5,20}$/i)
const FOUR_TO_FIFTEEN_DIGITS_WITH_OPTIONAL_PLUS_SIGN_AT_THE_FRONT =
  helpers.regex(/^\+?[0-9- ]{4,15}$/i)

const { t, locale } = useI18n()
const userStore = useUserStore()
const { acceptedTerms, email } = storeToRefs(userStore)
const router = useRouter()

const isPerson = ref(true)
const isFeedbackModalActive = ref(false)
const submitIsLoading = ref(false)
const feedbackSubTitle = ref(t("errors.defaultError.subTitle"))

const form = reactive<AccountingInfo>({
  firstName: "",
  lastName: "",
  address: "",
  city: "",
  postCode: "",
  country: "HU",
  companyName: "",
  taxNumber: "",
  groupCode: "",
  phoneNumber: ""
})

const formValidationParams = {
  firstName: {
    minLength: 2,
    maxLength: 60
  },
  lastName: {
    minLength: 2,
    maxLength: 60
  },
  address: {
    minLength: 2,
    maxLength: 60
  },
  city: {
    minLength: 2,
    maxLength: 60
  },
  postCode: {
    minLength: 4,
    maxLength: 12
  },
  country: {
    minLength: 2,
    maxLength: 60
  },
  companyName: {
    minLength: 5,
    maxLength: 150
  },
  taxNumber: {
    minLength: 5,
    maxLength: 20
  },
  groupCode: {
    minLength: 5,
    maxLength: 20
  },
  phoneNumber: {
    minLength: 4,
    maxLength: 15
  }
}

const withLength = (field: keyof typeof formValidationParams) => {
  const { minLength: min, maxLength: max } = formValidationParams[field]
  return { minLength: minLength(min), maxLength: maxLength(max) }
}

const rules = computed(() => {
  return {
    form: {
      firstName: {
        TWO_TO_SIXTY_LETTERS_WITHOUT_DIGITS_AND_SOME_SPECIAL_CHARACTERS,
        required: requiredIf(() => isPerson.value),
        ...withLength("firstName")
      },
      lastName: {
        TWO_TO_SIXTY_LETTERS_WITHOUT_DIGITS_AND_SOME_SPECIAL_CHARACTERS,
        required: requiredIf(() => isPerson.value),
        ...withLength("lastName")
      },
      address: {
        required,
        TWO_TO_SIXTY_LETTERS_WITHOUT_SOME_SPECIAL_CHARACTERS,
        ...withLength("address")
      },
      city: {
        required,
        TWO_TO_SIXTY_LETTERS_WITHOUT_DIGITS_AND_SOME_SPECIAL_CHARACTERS,
        ...withLength("city")
      },
      postCode: {
        regex: FOUR_TO_TWELVE_LETTERS_OR_DIGITS_OR_HYPHENS,
        required
      },
      country: {
        required
      },
      companyName: {
        requiredIf: requiredIf(() => !isPerson.value),
        FIVE_TO_150_LETTERS_WITHOUT_SOME_SPECIAL_CHARACTERS,
        ...withLength("companyName")
      },
      taxNumber: {
        requiredIf: requiredIf(() => !isPerson.value),
        regex: FIVE_TO_TWENTY_LETTERS_OR_DIGITS_OR_HYPHENS
      },
      groupCode: {
        FIVE_TO_TWENTY_DIGITS,
        ...withLength("groupCode")
      },
      phoneNumber: {
        FOUR_TO_FIFTEEN_DIGITS_WITH_OPTIONAL_PLUS_SIGN_AT_THE_FRONT,
        ...withLength("phoneNumber"),
        required: requiredIf(() => isPerson.value)
      }
    }
  }
})

const v$ = useVuelidate(rules, { form })

const onSubmit = async () => {
  if (v$.value.form.$invalid) {
    console.log("RegistrationPage onSubmit clicked, but the form is invalid!")
    return
  }

  Object.assign(form, stringNullify(form))

  const selectedCountry = countries.find(
    (country) => country.code === form.country
  )
  if (selectedCountry) {
    form.country = getCountryName(selectedCountry, locale.value)
  }

  submitIsLoading.value = true

  try {
    const { data } = await GuestChargingService.setAccountingInfo(form)
    window.location.href = data.simplePayUrl
  } catch (e: any) {
    console.error(e)
    feedbackSubTitle.value = t("errors.defaultError.subTitle", {
      errorMessage: e.message
    })
    isFeedbackModalActive.value = true
  } finally {
    submitIsLoading.value = false
  }
}

const handleFeedbackModalOkClick = () => {
  isFeedbackModalActive.value = false
}

onMounted(() => {
  if (!acceptedTerms.value) {
    console.log("User has not accepted the terms! Redirecting..")
    router.push("terms")
  }
  if (!email.value) {
    console.log("User has not given the e-mail address! Redirecting..")
    router.push("terms")
  }
})
</script>
